import React from 'react'
import { cn } from '@/lib/utils'

interface NavProps extends React.HTMLAttributes<HTMLUListElement> {
  tabs?: boolean
  pills?: boolean
  vertical?: boolean
}

export const Nav: React.FC<NavProps> = ({ tabs, pills, className, children, ...props }) => (
  <ul
    className={cn(
      'list-none m-0',
      tabs
        ? 'inline-flex h-10 items-center rounded-lg bg-muted p-1 text-muted-foreground gap-1'
        : 'flex p-0',
      pills && 'gap-1',
      className,
    )}
    {...props}
  >
    {children}
  </ul>
)

interface NavItemProps extends React.HTMLAttributes<HTMLLIElement> {}

export const NavItem: React.FC<NavItemProps> = ({ className, ...props }) => (
  <li className={cn(className)} {...props} />
)

interface NavLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean
  disabled?: boolean
  tag?: React.ElementType
}

export const NavLink: React.FC<NavLinkProps> = ({
  active,
  disabled,
  className,
  children,
  tag: Tag = 'a',
  style,
  ...props
}) => {
  // Support both `active` prop and `.active` in className (Bootstrap pattern)
  const isActive = active || (typeof className === 'string' && className.includes('active'))
  const cleanClassName = typeof className === 'string' ? className.replace(/\bactive\b/, '').trim() : className

  return (
    <Tag
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all cursor-pointer !no-underline',
        isActive
          ? 'bg-background text-foreground shadow'
          : 'text-muted-foreground hover:text-foreground hover:bg-background/80',
        disabled && 'opacity-50 pointer-events-none',
        cleanClassName,
      )}
      style={isActive ? undefined : style}
      {...props}
    >
      {children}
    </Tag>
  )
}

interface TabContentProps extends React.HTMLAttributes<HTMLDivElement> {
  activeTab?: string
}

interface TabPaneProps extends React.HTMLAttributes<HTMLDivElement> {
  tabId?: string
}

export const TabContent: React.FC<TabContentProps> = ({ activeTab, children, className, ...props }) => (
  <div className={cn('mt-3', className)} {...props}>
    {React.Children.map(children, (child) => {
      if (React.isValidElement<TabPaneProps>(child) && child.props.tabId === activeTab) {
        return child
      }
      return null
    })}
  </div>
)

export const TabPane: React.FC<TabPaneProps> = ({ className, tabId, ...props }) => (
  <div className={cn('rounded-lg border bg-card p-6 shadow-sm', className)} {...props} />
)
